/**
 * The results' note of items changed after people voted (canon 11 §2.2).
 *
 * Every such change is recorded by the database (session_item_changes,
 * migration 1063), whichever rule let it through: under "flag" the votes stand
 * as cast and this note is the warning canon asks for; under the other rules it
 * says what was cleared or re-asked. Draws nothing when nothing changed.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useUserNameMap } from '../../../hooks/useUserNameMap';

interface ItemChange {
  id: string;
  rule: 'invalidate' | 'remediate' | 'flag';
  added: Array<{ id: string; title?: string }>;
  removed: Array<{ id: string; title?: string }>;
  responses_cleared: number;
  responses_kept: number;
  changed_by: string | null;
  changed_at: string;
}

const RULE_OUTCOME: Record<ItemChange['rule'], (c: ItemChange) => string> = {
  flag: (c) => `${c.responses_kept === 1 ? 'The 1 vote was' : `All ${c.responses_kept} votes were`} kept as cast`,
  invalidate: (c) => `${c.responses_cleared === 1 ? 'The 1 vote was' : `All ${c.responses_cleared} votes were`} cleared and voters asked to vote again`,
  remediate: (c) =>
    `Voters kept their other answers and were asked about the new items` +
    (c.responses_cleared > 0 ? `; ${c.responses_cleared} ranking ${c.responses_cleared === 1 ? 'vote was' : 'votes were'} cleared` : ''),
};

function titles(list: Array<{ id: string; title?: string }>): string {
  return list.map((i) => `"${i.title ?? i.id}"`).join(', ');
}

export function SessionItemChangesNote({ sessionId, className = '' }: { sessionId: string; className?: string }) {
  const [changes, setChanges] = useState<ItemChange[]>([]);
  const names = useUserNameMap(changes.map((c) => c.changed_by));

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !sessionId) return;
    let cancelled = false;
    supabase
      .from('session_item_changes')
      .select('id, rule, added, removed, responses_cleared, responses_kept, changed_by, changed_at')
      .eq('session_id', sessionId)
      .order('changed_at', { ascending: true })
      .then(({ data }) => {
        if (!cancelled) setChanges((data ?? []) as ItemChange[]);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (changes.length === 0) return null;

  return (
    <div role="note" className={`border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3 ${className}`}>
      <p className="flex items-center gap-2 text-sm font-medium text-[var(--text-pri)]">
        <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" aria-hidden />
        Items changed after voting began
      </p>
      <ul className="mt-1 space-y-1 text-sm text-[var(--text-sec)]">
        {changes.map((c) => (
          <li key={c.id}>
            {new Date(c.changed_at).toLocaleString()}
            {c.changed_by ? `, ${names[c.changed_by] ?? 'someone'}` : ''}:{' '}
            {c.added.length > 0 && `added ${titles(c.added)}`}
            {c.added.length > 0 && c.removed.length > 0 && '; '}
            {c.removed.length > 0 && `removed ${titles(c.removed)}`}. {RULE_OUTCOME[c.rule](c)}.
          </li>
        ))}
      </ul>
    </div>
  );
}
